/**
 * Shared presentational bits for the Sites & Operators tabs (Task 24.3 / DEC-094).
 * Inline-style idiom matching ManagementPage / management form-bits.
 */
import * as React from 'react';
import { Plus } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { StatusLabel } from 'src/components/ui/status-label';
import { formatNumber } from '@lodex-pro/shared/number-format';

export const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-border-secondary)',
  fontSize: '14px',
};

export const subText: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-tertiary)',
};

export const searchInputStyle: React.CSSProperties = {
  width: '100%',
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
  marginBottom: '12px',
};

type PillTone = 'neutral' | 'positive' | 'muted' | 'accent';

const PILL_COLORS: Record<'neutral' | 'accent', { bg: string; fg: string }> = {
  neutral: { bg: 'var(--color-bg-tertiary)', fg: 'var(--color-text-secondary)' },
  accent: { bg: 'rgba(123, 150, 232, 0.12)', fg: 'var(--color-link)' },
};

export function Pill({
  label,
  tone = 'neutral',
}: {
  label: string;
  tone?: PillTone;
}): React.JSX.Element {
  // Web restyle: a status (positive / muted) is a dot plus its word; a tag (neutral / accent,
  // e.g. a company or a slot) stays a chip.
  if (tone === 'positive' || tone === 'muted') {
    return (
      <StatusLabel tone={tone === 'positive' ? 'success' : 'neutral'} style={{ fontSize: '12px' }}>
        {label}
      </StatusLabel>
    );
  }
  const c = PILL_COLORS[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
      }}
    >
      {label}
    </span>
  );
}

export function ActiveBadge({ active }: { active: boolean }): React.JSX.Element {
  return <Pill label={active ? 'Active' : 'Inactive'} tone={active ? 'positive' : 'muted'} />;
}

export function AddButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        backgroundColor: 'var(--color-accent-blue)',
        color: 'var(--color-on-accent)',
        borderColor: 'var(--color-accent-blue)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        opacity: disabled === true ? 0.5 : 1,
      }}
    >
      <Plus size={16} aria-hidden="true" /> {label}
    </Button>
  );
}

export function SectionHeader({
  title,
  count,
  action,
}: {
  title: string;
  count?: number;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
      }}
    >
      <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {title}
        {count !== undefined && (
          <span style={{ ...subText, marginLeft: '8px' }}>({formatNumber(count)})</span>
        )}
      </h2>
      {action}
    </div>
  );
}
